/**
 * LOCATION: dashboard-business/src/components/Sidebar.js
 *
 * FIXES:
 * 1. Hamburger moved INSIDE main layout — no longer fixed/overlapping
 * 2. Sidebar fully scrollable — logout always visible
 * 3. Logo uses process.env.PUBLIC_URL — works on Render
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthService from '../services/auth';

const NAV = [
  { path: '/dashboard', label: 'Dashboard',  icon: 'fa-gauge',       pro: false },
  { path: '/chats',     label: 'Live Chats', icon: 'fa-comments',    pro: true  },
  { path: '/agents',    label: 'Agents',     icon: 'fa-users',       pro: true  },
  { path: '/settings',  label: 'Settings',   icon: 'fa-gear',        pro: false },
  { path: '/billing',   label: 'Billing',    icon: 'fa-credit-card', pro: false },
  { path: '/analytics', label: 'Analytics',  icon: 'fa-chart-line',  pro: false },
];

const PLAN_LABEL = { basic: 'Basic', free: 'Basic', pro: 'Pro', PREMIUM: 'PREMIUM' };
const PLAN_COLOR = { basic: '#6b7280', free: '#6b7280', pro: '#667eea', PREMIUM: '#8b5cf6' };
const PLAN_ICON  = { basic: 'fa-seedling', free: 'fa-seedling', pro: 'fa-bolt', PREMIUM: 'fa-crown' };

// ── TOPBAR — rendered above page content on mobile ─────────────
// Import and render this in your page components on mobile
export function MobileTopbar({ onOpen, title }) {
  return (
    <div style={{
      display: 'none',
      // shown via CSS on mobile
    }} className="sb-topbar">
      <button onClick={onOpen} className="sb-topbar-btn">
        <i className="fa-solid fa-bars"></i>
      </button>
      {title && <span className="sb-topbar-title">{title}</span>}
    </div>
  );
}

export default function Sidebar() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const user      = AuthService.getCurrentUser();
  const rawPlan   = user?.plan || 'basic';
  const plan      = rawPlan === 'free' ? 'basic' : rawPlan;
  const canPro    = plan === 'pro' || plan === 'PREMIUM';

  const [open,  setOpen]  = useState(false);
  const [tip,   setTip]   = useState(null);
  const sideRef = useRef(null);

  useEffect(() => {
    if (!document.getElementById('fa-sb')) {
      const fa = document.createElement('link');
      fa.id = 'fa-sb'; fa.rel = 'stylesheet';
      fa.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css';
      document.head.appendChild(fa);
    }
  }, []);

  useEffect(() => { setOpen(false); setTip(null); }, [location.pathname]);

  useEffect(() => {
    const h = (e) => {
      if (open && sideRef.current && !sideRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  // Lock body scroll when sidebar open on mobile
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const handleNav = (item) => {
    if (item.pro && !canPro) {
      setTip(tip === item.path ? null : item.path);
      return;
    }
    navigate(item.path);
  };

  const handleLogout = () => { AuthService.logout(); navigate('/login'); };

  const planColor = PLAN_COLOR[plan];
  const planLabel = PLAN_LABEL[plan];

  return (
    <>
      <style>{`
        /* ── Global layout ──────────────────────────────── */
        .sb-layout {
          display: flex;
          min-height: 100vh;
        }

        /* ── Sidebar root ───────────────────────────────── */
        .sb-root {
          width: 240px;
          min-width: 240px;
          height: 100vh;
          background: #fff;
          border-right: 1px solid #e5e7eb;
          display: flex;
          flex-direction: column;
          position: sticky;
          top: 0;
          flex-shrink: 0;
          overflow: hidden; /* outer clips */
          z-index: 50;
        }

        /* inner scroll container */
        .sb-inner {
          display: flex;
          flex-direction: column;
          height: 100%;
          padding: 18px 14px;
          overflow-y: auto;
          overflow-x: hidden;
          /* custom scrollbar */
          scrollbar-width: thin;
          scrollbar-color: #e5e7eb transparent;
        }
        .sb-inner::-webkit-scrollbar { width: 4px; }
        .sb-inner::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 2px; }

        /* ── Overlay ────────────────────────────────────── */
        .sb-overlay {
          display: none;
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.5);
          z-index: 198;
          backdrop-filter: blur(2px);
        }
        .sb-overlay.show { display: block; }

        /* ── Floating hamburger (mobile only) ───────────── */
        .sb-hamburger {
          display: none;
          position: fixed;
          top: 14px;
          left: 14px;
          width: 42px;
          height: 42px;
          border: 1px solid #e5e7eb;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 150;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          font-size: 17px;
          color: #374151;
        }

        /* ── Mobile topbar (hamburger row) ──────────────── */
        .sb-topbar {
          display: none;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: #fff;
          border-bottom: 1px solid #e5e7eb;
          position: sticky;
          top: 0;
          z-index: 40;
          box-shadow: 0 1px 4px rgba(0,0,0,0.06);
        }
        .sb-topbar-btn {
          width: 38px;
          height: 38px;
          background: #f3f4f6;
          border: none;
          border-radius: 9px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 16px;
          color: #374151;
          flex-shrink: 0;
        }
        .sb-topbar-greeting {
          font-size: 14px;
          font-weight: 700;
          color: #111827;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .sb-topbar-plan {
          margin-left: auto;
          font-size: 10px;
          font-weight: 800;
          padding: 3px 8px;
          border-radius: 20px;
          flex-shrink: 0;
        }

        /* ── Close button inside sidebar (mobile only) ── */
        .sb-close-btn {
          display: none;
          background: #f3f4f6;
          border: none;
          border-radius: 8px;
          width: 30px;
          height: 30px;
          cursor: pointer;
          align-items: center;
          justify-content: center;
          color: #6b7280;
          font-size: 14px;
          flex-shrink: 0;
        }

        /* ── Nav items ──────────────────────────────────── */
        .sb-nav-btn {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 9px;
          padding: 9px 11px;
          background: transparent;
          border: none;
          border-radius: 9px;
          cursor: pointer;
          text-align: left;
          transition: background 0.15s;
        }
        .sb-nav-btn:hover { background: #f9fafb; }
        .sb-nav-btn.active { background: #f0f1ff; }

        .sb-nav-icon {
          width: 30px;
          height: 30px;
          border-radius: 8px;
          background: #f3f4f6;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .sb-nav-btn.active .sb-nav-icon { background: #667eea20; }

        /* ── User row at bottom ─────────────────────────── */
        .sb-user-row {
          margin-top: auto;
          padding-top: 12px;
          border-top: 1px solid #f3f4f6;
          /* ensure it's always visible — the scroll container handles overflow */
          flex-shrink: 0;
        }

        /* ── Upgrade tooltip ────────────────────────────── */
        .sb-tip {
          position: absolute;
          left: 106%;
          top: 50%;
          transform: translateY(-50%);
          background: #111827;
          color: #fff;
          padding: 10px 14px;
          border-radius: 10px;
          font-size: 12px;
          width: 200px;
          z-index: 999;
          box-shadow: 0 8px 24px rgba(0,0,0,0.3);
          line-height: 1.5;
        }

        /* ── MOBILE BREAKPOINT ──────────────────────────── */
        @media (max-width: 768px) {
          /* Hide desktop sidebar, show topbar */
          .sb-root {
            position: fixed !important;
            top: 0; left: 0;
            height: 100vh;
            transform: translateX(-260px);
            transition: transform 0.28s cubic-bezier(0.4,0,0.2,1);
            box-shadow: 4px 0 24px rgba(0,0,0,0.18);
            z-index: 200;
            min-width: 0;
            width: 240px;
          }
          .sb-root.open {
            transform: translateX(0);
          }
          .sb-close-btn { display: flex !important; }
          .sb-topbar { display: flex !important; }
          .sb-hamburger { display: flex !important; }
          .sb-hamburger.hide { display: none !important; }

          /* tip goes below on mobile since there's no room to the right */
          .sb-tip {
            left: 0;
            top: calc(100% + 6px);
            transform: none;
            width: 220px;
          }
        }
      `}</style>

      {/* ── Floating hamburger (mobile) ── */}
      <button
        className={`sb-hamburger${open ? ' hide' : ''}`}
        onClick={() => setOpen(true)}
        aria-label="Open menu"
      >
        <i className="fa-solid fa-bars"></i>
      </button>

      {/* ── Overlay ── */}
      <div className={`sb-overlay${open ? ' show' : ''}`} onClick={() => setOpen(false)} />

      {/* ── Sidebar ── */}
      <aside ref={sideRef} className={`sb-root${open ? ' open' : ''}`}>
        <div className="sb-inner">

          {/* Logo row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, paddingBottom: 14, borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
              <img
                src={`${process.env.PUBLIC_URL}/l.png`}
                alt="Janimony"
                style={{ width: 90, height: 'auto', objectFit: 'contain' }}
                onError={e => { e.target.style.display = 'none'; }}
              />
              <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, marginTop: 6 }}>Business Dashboard</div>
            </div>
            <button className="sb-close-btn" onClick={() => setOpen(false)}>
              <i className="fa-solid fa-xmark"></i>
            </button>
          </div>

          {/* Plan badge */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 11px', borderRadius: 9, background: planColor + '12', border: `1px solid ${planColor}25`, marginBottom: 14, flexShrink: 0 }}>
            <i className={`fa-solid ${PLAN_ICON[plan]}`} style={{ color: planColor, fontSize: 12 }}></i>
            <span style={{ fontSize: 12, fontWeight: 700, color: planColor }}>{planLabel} Plan</span>
            {plan === 'basic' && (
              <button onClick={() => navigate('/billing')}
                style={{ marginLeft: 'auto', padding: '2px 8px', background: '#667eea', color: '#fff', border: 'none', borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
                Upgrade
              </button>
            )}
          </div>

          {/* Nav */}
          <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {NAV.map(item => {
              const active  = location.pathname === item.path;
              const locked  = item.pro && !canPro;
              const showTip = tip === item.path;
              return (
                <div key={item.path} style={{ position: 'relative' }}>
                  <button
                    onClick={() => handleNav(item)}
                    className={`sb-nav-btn${active ? ' active' : ''}`}
                    style={{ opacity: locked ? 0.55 : 1, cursor: locked ? 'not-allowed' : 'pointer' }}
                  >
                    <div className="sb-nav-icon">
                      <i className={`fa-solid ${item.icon}`} style={{ fontSize: 13, color: active ? '#667eea' : locked ? '#d1d5db' : '#6b7280' }}></i>
                    </div>
                    <span style={{ flex: 1, fontSize: 13, fontWeight: active ? 700 : 500, color: active ? '#111827' : locked ? '#9ca3af' : '#374151' }}>
                      {item.label}
                    </span>
                    {locked && <i className="fa-solid fa-lock" style={{ fontSize: 10, color: '#d1d5db' }}></i>}
                    {active  && <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#667eea', flexShrink: 0 }}></div>}
                  </button>

                  {locked && showTip && (
                    <div className="sb-tip">
                      <i className="fa-solid fa-lock" style={{ marginRight: 6, color: '#fbbf24' }}></i>
                      <strong>{item.label}</strong> is available on Pro and Premium plans.
                      <button onClick={() => navigate('/billing')}
                        style={{ display: 'block', marginTop: 8, padding: '5px 10px', background: '#667eea', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', width: '100%' }}>
                        <i className="fa-solid fa-arrow-up" style={{ marginRight: 5 }}></i>Upgrade Now
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* User + logout — always visible because sb-inner scrolls */}
          <div className="sb-user-row">
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,#667eea,#764ba2)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13, flexShrink: 0 }}>
                {(user?.businessName || user?.email || 'U')[0].toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user?.businessName || 'Business'}
                </div>
                <div style={{ fontSize: 11, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user?.email || ''}
                </div>
              </div>
              <button onClick={handleLogout}
                style={{ background: '#fee2e2', border: 'none', borderRadius: 7, width: 30, height: 30, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444', fontSize: 13, flexShrink: 0 }}
                title="Logout">
                <i className="fa-solid fa-right-from-bracket"></i>
              </button>
            </div>
          </div>

        </div>
      </aside>
    </>
  );
}

/**
 * MOBILE TOPBAR COMPONENT
 * ─────────────────────────────────────────────────────────────
 * Add this at the TOP of every page component that uses Sidebar.
 * It shows the hamburger + greeting on mobile without overlapping content.
 *
 * Usage in any page:
 *
 *   import Sidebar, { SidebarTopbar } from '../components/Sidebar';
 *
 *   const MyPage = () => {
 *     const [sidebarOpen, setSidebarOpen] = React.useState(false);
 *     return (
 *       <div style={{ display: 'flex', minHeight: '100vh' }}>
 *         <Sidebar externalOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
 *         <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
 *           <SidebarTopbar onOpen={() => setSidebarOpen(true)} title="Dashboard" />
 *           <main style={{ flex: 1, padding: 32 }}>
 *             ... your content ...
 *           </main>
 *         </div>
 *       </div>
 *     );
 *   };
 *
 * ─────────────────────────────────────────────────────────────
 * OR — simpler approach: just use the self-contained version below
 * which handles its own open state internally:
 */
export function SidebarWithTopbar({ children, title }) {
  const [open, setOpen] = useState(false);
  const user = AuthService.getCurrentUser();

  return (
    <>
      <style>{`
        .sbt-layout { display: flex; min-height: 100vh; font-family: 'DM Sans', sans-serif; }
        .sbt-body { flex: 1; display: flex; flex-direction: column; min-width: 0; overflow: hidden; }
        .sbt-topbar {
          display: none;
          align-items: center;
          gap: 12px;
          padding: 11px 16px;
          background: #fff;
          border-bottom: 1px solid #e5e7eb;
          position: sticky;
          top: 0;
          z-index: 40;
          box-shadow: 0 1px 4px rgba(0,0,0,0.05);
        }
        .sbt-topbar-btn {
          width: 38px; height: 38px;
          background: #f3f4f6;
          border: none; border-radius: 9px;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; font-size: 16px; color: #374151;
          flex-shrink: 0;
        }
        .sbt-greeting { font-size: 14px; font-weight: 700; color: #111827; }
        .sbt-main { flex: 1; overflow-y: auto; }
        @media (max-width: 768px) {
          .sbt-topbar { display: flex !important; }
        }
      `}</style>

      <div className="sbt-layout">
        <SidebarControlled open={open} onClose={() => setOpen(false)} />
        <div className="sbt-body">
          <div className="sbt-topbar">
            <button className="sbt-topbar-btn" onClick={() => setOpen(true)}>
              <i className="fa-solid fa-bars"></i>
            </button>
            <span className="sbt-greeting">
              {title || `Good morning, ${(user?.businessName || user?.email || 'there').split(' ')[0]}`}
            </span>
          </div>
          <div className="sbt-main">
            {children}
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Controlled sidebar — accepts open/onClose from parent
 * Used by SidebarWithTopbar above
 */
function SidebarControlled({ open, onClose }) {
  const navigate  = useNavigate();
  const location  = useLocation();
  const user      = AuthService.getCurrentUser();
  const rawPlan   = user?.plan || 'basic';
  const plan      = rawPlan === 'free' ? 'basic' : rawPlan;
  const canPro    = plan === 'pro' || plan === 'PREMIUM';

  const [tip, setTip] = useState(null);
  const sideRef = useRef(null);

  useEffect(() => { onClose(); setTip(null); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const handleNav = (item) => {
    if (item.pro && !canPro) { setTip(tip === item.path ? null : item.path); return; }
    navigate(item.path);
  };

  const planColor = PLAN_COLOR[plan];
  const planLabel = PLAN_LABEL[plan];

  return (
    <>
      <div className={`sb-overlay${open ? ' show' : ''}`} onClick={onClose} />
      <aside ref={sideRef} className={`sb-root${open ? ' open' : ''}`}>
        <div className="sb-inner">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, paddingBottom: 14, borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
              <img src={`${process.env.PUBLIC_URL}/l.png`} alt="Janimony"
                style={{ width: 90, height: 'auto', objectFit: 'contain' }}
                onError={e => { e.target.style.display = 'none'; }} />
              <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, marginTop: 6 }}>Business Dashboard</div>
            </div>
            <button className="sb-close-btn" onClick={onClose}>
              <i className="fa-solid fa-xmark"></i>
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 11px', borderRadius: 9, background: planColor + '12', border: `1px solid ${planColor}25`, marginBottom: 14, flexShrink: 0 }}>
            <i className={`fa-solid ${PLAN_ICON[plan]}`} style={{ color: planColor, fontSize: 12 }}></i>
            <span style={{ fontSize: 12, fontWeight: 700, color: planColor }}>{planLabel} Plan</span>
            {plan === 'basic' && (
              <button onClick={() => navigate('/billing')} style={{ marginLeft: 'auto', padding: '2px 8px', background: '#667eea', color: '#fff', border: 'none', borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
                Upgrade
              </button>
            )}
          </div>
          <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {NAV.map(item => {
              const active  = location.pathname === item.path;
              const locked  = item.pro && !canPro;
              const showTip = tip === item.path;
              return (
                <div key={item.path} style={{ position: 'relative' }}>
                  <button onClick={() => handleNav(item)}
                    className={`sb-nav-btn${active ? ' active' : ''}`}
                    style={{ opacity: locked ? 0.55 : 1, cursor: locked ? 'not-allowed' : 'pointer' }}>
                    <div className="sb-nav-icon">
                      <i className={`fa-solid ${item.icon}`} style={{ fontSize: 13, color: active ? '#667eea' : locked ? '#d1d5db' : '#6b7280' }}></i>
                    </div>
                    <span style={{ flex: 1, fontSize: 13, fontWeight: active ? 700 : 500, color: active ? '#111827' : locked ? '#9ca3af' : '#374151' }}>{item.label}</span>
                    {locked && <i className="fa-solid fa-lock" style={{ fontSize: 10, color: '#d1d5db' }}></i>}
                    {active  && <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#667eea', flexShrink: 0 }}></div>}
                  </button>
                  {locked && showTip && (
                    <div className="sb-tip">
                      <i className="fa-solid fa-lock" style={{ marginRight: 6, color: '#fbbf24' }}></i>
                      <strong>{item.label}</strong> is available on Pro and Premium plans.
                      <button onClick={() => navigate('/billing')} style={{ display: 'block', marginTop: 8, padding: '5px 10px', background: '#667eea', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', width: '100%' }}>
                        <i className="fa-solid fa-arrow-up" style={{ marginRight: 5 }}></i>Upgrade Now
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
          <div className="sb-user-row">
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,#667eea,#764ba2)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13, flexShrink: 0 }}>
                {(user?.businessName || user?.email || 'U')[0].toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.businessName || 'Business'}</div>
                <div style={{ fontSize: 11, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.email || ''}</div>
              </div>
              <button onClick={() => { AuthService.logout(); navigate('/login'); }}
                style={{ background: '#fee2e2', border: 'none', borderRadius: 7, width: 30, height: 30, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444', fontSize: 13, flexShrink: 0 }}
                title="Logout">
                <i className="fa-solid fa-right-from-bracket"></i>
              </button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}